import { useEffect, useRef, useState } from "react";
import { Box, Flex, Text, Heading, Button, Spinner, AspectRatio } from "@/ui";
import useToast from "@/hooks/useToast";
import { useControls } from "@/hooks";
import { t } from "@/utils";
import Time from "@/lib/Time";
import Modal from "@/components/Modal";
import IconButton from "@/components/IconButton";
import { useDevices } from "@/store/devices";
import Circle from "@/components/Circle";
import Icon from "@/components/Icon";
import { useViewer } from "viewer/store/viewer";
import { getCurrentCameraConfig } from "viewer/store/viewer/selectors";
import { getCameraHistoryEventByUniqueId, prepareCameraHistoryEventPlaylist } from "viewer/modules/cloud/media";
import EventPlayer from "viewer/modules/activityLog/components/EventPlayer";
import CheckIcon from "assets/icons/check.svg?react";
import Close from "assets/icons/close_large.svg?react";
import CameraIcon from "assets/icons/camera.svg?react";

export default function ManualRecordingViewer() {
  const [isPreparingPlaylist, setIsPreparingPlaylist] = useState(false);
  const [playlistUrl, setPlaylistUrl] = useState<string | null>(null);
  const [currentEvent, setCurrentEvent] = useState<CameraEvent | null>(null);

  const { show } = useToast();
  const playerModal = useControls();
  const cameraConfig = useViewer(getCurrentCameraConfig);
  const devices = useDevices((s) => s.devices);

  const videoRecordingStatus = cameraConfig?.videoRecordingStatus;
  const isRecording = Boolean(videoRecordingStatus?.recordingOn);
  const prevIsRecording = useRef(isRecording);

  const device = cameraConfig?.jid ? devices[cameraConfig.jid] : null;

  useEffect(() => {
    if (prevIsRecording.current && !isRecording && cameraConfig) {
      const handleView = async () => {
        setIsPreparingPlaylist(true);

        const event = videoRecordingStatus?.recordingOff?.event;
        const jid = cameraConfig?.jid;

        if (!event || !jid) return;

        try {
          setCurrentEvent(event);
          playerModal.open();

          const fetchedEvent = await getCameraHistoryEventByUniqueId({
            uniqueId: event.uniqueId,
            jid
          });
          if (!fetchedEvent?.objectId) {
            playerModal.close();
            return;
          }

          const result = await prepareCameraHistoryEventPlaylist(fetchedEvent.objectId);

          setPlaylistUrl(result.playlistUrl);
        } catch (err) {
          playerModal.close();
        } finally {
          setIsPreparingPlaylist(false);
        }
      };

      show({
        title: t("savedToEvents"),
        icon: CheckIcon,
        iconFill: "green",
        customComponent: (
          <Button variant="textFixedSmall" onClick={handleView}>
            {t("view")}
          </Button>
        )
      });
    }

    prevIsRecording.current = isRecording;
  }, [isRecording, videoRecordingStatus, show, cameraConfig?.jid, playerModal, isPreparingPlaylist, cameraConfig]);

  const handleClose = () => {
    playerModal.close();
    setCurrentEvent(null);
    setPlaylistUrl(null);
  };

  return (
    <Modal isOpen={playerModal.isOpen} onClose={handleClose} modalBodyProps={{ p: 0, borderRadius: "1rem" }}>
      <Box pos="relative">
        <IconButton
          icon={Close}
          tooltip={{ label: t("buttons.close") }}
          bg="rgba(68, 70, 79, 0.7)"
          props={{
            pos: "absolute",
            right: "24",
            top: "24",
            zIndex: 2
          }}
          onClick={handleClose}
        />
        <Box borderRadius="1rem">
          {isPreparingPlaylist ? (
            <AspectRatio ratio={16 / 9}>
              <Flex
                alignItems="center"
                justifyContent="center"
                gap="0.75rem"
                borderRadius="1rem"
                bgImage="linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.20) 97.16%)"
              >
                <Spinner size="lg" />
                <Text variant="label" color="on-surface">
                  {t("processing")}
                </Text>
              </Flex>
            </AspectRatio>
          ) : (
            <EventPlayer url={playlistUrl || undefined} playOnLoad />
          )}
          {currentEvent && (
            <Flex flexDir="column" px={["1.5rem", "1rem", "1.5rem"]} pt="1rem" pb="1.5rem" minH="164px">
              <Flex mb="0.75rem" alignItems="center" columnGap="0.75rem">
                <Circle size="2rem" bg="secondary-container">
                  <Icon icon={CameraIcon} fill="secondary" size="16" />
                </Circle>
                <Heading as="h5">
                  {Time.formatDate(currentEvent.start, "HH:mm")} · {t("recordedClip")}
                </Heading>
              </Flex>
              <Flex justifyContent="space-between" flexWrap="wrap" gap="0.75rem">
                <Flex>
                  <Box mr="0.75rem">
                    <Text color="on-surface">{t("duration")}</Text>
                    <Text color="on-surface">{t("interval")}</Text>
                    <Text color="on-surface">{t("camera")}</Text>
                  </Box>
                  <Box color="on-surface-variant">
                    <Text>{Time.formatDuration(currentEvent.end - currentEvent.start)}</Text>
                    <Text>
                      {Time.formatDate(currentEvent.start, "HH:mm:ss") + " - "}
                      {Time.formatDate(currentEvent.end, "HH:mm:ss")}
                    </Text>
                    <Text noOfLines={1} maxW={250}>
                      {device?.name || "unknown"}
                    </Text>
                  </Box>
                </Flex>
              </Flex>
            </Flex>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
